// =============================================================================
// features/auth/useFirstAccess.ts — Dados do primeiro acesso (F56-S02).
//
//   useResolveFirstAccess  → GET /api/auth/me: descobre se o primeiro acesso está pendente
//                            quando a sessão foi restaurada por refresh (reload da página).
//   useFirstAccessSubmit   → POST /api/auth/first-access: conclui a troca e aplica a sessão nova.
//
// Segurança (LGPD + auth):
//   - O estado "pendente" vem SEMPRE do servidor (login, /me) e vive só na memória do store.
//   - Senhas (atual e nova) passam só pelo corpo da requisição: nada de storage, log ou URL.
//     A mutation usa `gcTime: 0` e é resetada ao concluir/desmontar, para o TanStack Query
//     não reter as `variables` (que contêm as senhas) no MutationCache.
//   - INVALID_CURRENT_PASSWORD é 422 (não 401): não aciona o interceptor de refresh de lib/api.
//     Um 401 de verdade (token expirado enquanto a pessoa digitava) usa o caminho normal do
//     interceptor: UM refresh e UM retry, nunca laço. Depois do sucesso valem só os tokens novos
//     (a resposta traz sessão nova; as antigas foram revogadas pelo servidor).
//   - E-mail pessoal (F58-S15, D14): é gravado pela API de conta DEPOIS de a troca dar certo (com
//     o token novo, ainda antes de liberar o sistema, para o modal não piscar) e NUNCA vai no corpo
//     de /first-access. Falhar aqui não desfaz a troca: a mutation segue como sucesso e o guard
//     do modal cobra depois. O valor só existe em memória (variables da mutation + rascunho).
// =============================================================================

import { authMeResponseSchema, type FirstAccessBody } from '@elemento/shared-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useToast } from '../../components/ui/Toast';
import { api, apiLogout } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';
import { setPersonalEmail } from '../account/api';
import {
  rememberPersonalEmailDraft,
  savePersonalEmailBestEffort,
  type PersonalEmailSaveResult,
} from '../account/personalEmail';

import { decideFirstAccessResponse, isSameSessionUser, mapFirstAccessError } from './firstAccess';
import { mapResponseToUser } from './useAuth';

const ME_QUERY_KEY = ['auth', 'me'] as const;

// ─── Resolver o estado (reload) ───────────────────────────────────────────────

/**
 * Pergunta ao servidor se o primeiro acesso está pendente e grava a resposta no store.
 * `GET /api/auth/me` está na allowlist do usuário preso, então responde mesmo pendente.
 * Falha de rede/5xx NÃO libera o sistema: o guard mostra "tentar de novo" (falha fechada).
 */
export function useResolveFirstAccess(enabled: boolean) {
  const userId = useAuthStore((s) => s.user?.id ?? null);

  return useQuery({
    queryKey: [...ME_QUERY_KEY, userId],
    enabled: enabled && userId !== null,
    // Sem cache: um estado velho aqui reabriria o sistema para quem foi preso de novo.
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      const me = authMeResponseSchema.parse(await api.get<unknown>('/api/auth/me'));
      // Sessão mudou no meio do voo (logout/troca de usuário): NÃO deixa o estado em
      // 'unknown' com a query "bem-sucedida" (splash eterno). O erro cai no ResolveError.
      if (!isSameSessionUser(useAuthStore.getState().user?.id, me.id)) {
        throw new Error('session_changed');
      }
      useAuthStore.getState().setFirstAccess(me.must_change_credentials ? 'pending' : 'done');
      return me.must_change_credentials;
    },
  });
}

// ─── Concluir a troca ─────────────────────────────────────────────────────────

/**
 * 'done'   = sessão nova aplicada.
 * 'login'  = resposta fora do contrato: sessão encerrada no servidor, pede novo login.
 * 'reload' = idem, sem token utilizável (ou o logout falhou): recarrega para restaurar a sessão.
 */
export type FirstAccessOutcome = 'done' | 'login' | 'reload';

/** Dados do formulário: o corpo da API + o e-mail pessoal (que NÃO segue para /first-access). */
export type FirstAccessSubmitInput = FirstAccessBody & { personalEmail?: string };

export interface FirstAccessResult {
  outcome: FirstAccessOutcome;
  /** `skipped` fora do caso 'done' (sem sessão utilizável não há como gravar). */
  personalEmail: PersonalEmailSaveResult;
}

const SKIPPED: PersonalEmailSaveResult = { status: 'skipped' };

/**
 * Conclui o primeiro acesso. O componente decide a UX de erro com `mapFirstAccessError`;
 * aqui só tratamos o que muda a sessão: sucesso e "você já tinha concluído".
 */
export function useFirstAccessSubmit() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<FirstAccessResult, unknown, FirstAccessSubmitInput>({
    gcTime: 0,
    mutationFn: async ({ personalEmail, ...body }) => {
      const raw = await api.post<unknown>('/api/auth/first-access', body);
      // O backend serializa com `loginResponseSchema` (o literal `status: 'ok'` do
      // `firstAccessResponseSchema` é removido pelo serializador) — a decisão valida com ele.
      const decision = decideFirstAccessResponse(raw);
      if (decision.type === 'apply') {
        const { access_token, user } = decision.session;
        // Token novo na memória, mas o sistema ainda NÃO liberado: o e-mail pessoal é gravado
        // antes, para o guard do modal não consultar o perfil no meio da gravação.
        useAuthStore.getState().setAccessToken(access_token);
        const saved = await savePersonalEmailBestEffort(setPersonalEmail, personalEmail);
        // A gravação pode ter derrubado a sessão no meio (refresh falho => clear() no api.ts).
        // Restaurar o login aqui ressuscitaria uma sessão que o cliente já encerrou: a troca
        // de senha deu certo, então só mandamos entrar de novo (sem rascunho do e-mail).
        if (!isSameSessionUser(useAuthStore.getState().user?.id, user.id)) {
          return { outcome: 'login', personalEmail: SKIPPED };
        }
        if (saved.status === 'failed') rememberPersonalEmailDraft(user.id, saved.email);
        useAuthStore.getState().setAuth(mapResponseToUser({ user }), access_token, 'done');
        return { outcome: 'done', personalEmail: saved };
      }
      if (decision.type === 'logout') {
        // O servidor já trocou as credenciais e deixou cookies novos válidos: encerra a
        // sessão de verdade (o logout exige Bearer) antes de mandar para /login.
        useAuthStore.getState().setAccessToken(decision.accessToken);
        try {
          await apiLogout();
          return { outcome: 'login', personalEmail: SKIPPED };
        } catch {
          return { outcome: 'reload', personalEmail: SKIPPED };
        }
      }
      return { outcome: 'reload', personalEmail: SKIPPED };
    },
    onSuccess: ({ outcome, personalEmail }) => {
      // Nada do estado "preso" deve sobreviver: remove consultas (ex.: /me), mantém a mutation viva.
      queryClient.removeQueries();
      if (outcome === 'reload') {
        // A sessão nova segue válida nos cookies: o bootstrap a restaura de forma coerente.
        window.location.replace('/');
        return;
      }
      if (outcome === 'login') {
        useAuthStore.getState().clear();
        toast('Pronto! Agora entre com o seu e-mail e a senha nova.', 'info');
        navigate('/login', { replace: true });
        return;
      }
      if (personalEmail.status === 'failed') {
        // O primeiro acesso está feito; só o e-mail pessoal ficou de fora. O aviso do sistema
        // (modal) já traz o e-mail digitado de volta para tentar de novo.
        toast(
          'Seu e-mail e sua senha foram salvos, mas não conseguimos guardar o e-mail pessoal agora. Se o sistema pedir, é só confirmar.',
          'info',
        );
      } else if (personalEmail.status === 'saved') {
        toast('Tudo certo! Seu e-mail, sua senha e seu e-mail pessoal foram salvos.', 'success');
      } else {
        toast('Tudo certo! Seu e-mail e sua senha foram salvos.', 'success');
      }
      navigate('/', { replace: true });
    },
    onError: (error) => {
      // "Já concluiu": o servidor é a verdade. Libera o front e segue para o sistema.
      if (mapFirstAccessError(error).kind === 'already') {
        useAuthStore.getState().setFirstAccess('done');
        queryClient.removeQueries();
        navigate('/', { replace: true });
      }
    },
  });
}
